/**
 * Meeting Tools rail — CodeLive right sidebar (Figma NewFlowStudentLiveView).
 *
 * Flow:
 *   step 1) Render sections from getMeetingToolSections
 *   step 2) Tool click → onSelectTool
 *   step 3) Collapse / expand rail
 */

import React from "react";
import { ChevronLeft } from "lucide-react";
import { useScrollContainerWheel } from "../../utils/useScrollContainerWheel";
import {
  getMeetingToolSections,
  type LiveSessionRole,
  type MeetingToolId,
} from "./meetingToolSections";
import {
  MeetingToolsResizeHandle,
} from "./useMeetingToolsResize";

export type MeetingToolsRailProps = {
  role: LiveSessionRole;
  /** Primary trainer only — hides topic/recording tools for co-trainers. */
  canManageTopics?: boolean;
  collapsed: boolean;
  activeTool: MeetingToolId | null;
  badgeByTool?: Partial<Record<MeetingToolId, number>>;
  onSelectTool: (id: MeetingToolId) => void;
  onToggleCollapsed: () => void;
  /** Shared width from LiveSessionRoom (persists across tool open/back). */
  width: number;
  onResizePointerDown: (e: React.PointerEvent) => void;
  /** Bottom-sheet overlay for narrow viewports. */
  mobile?: boolean;
};

export function MeetingToolsRail({
  role,
  canManageTopics = false,
  collapsed,
  activeTool,
  badgeByTool,
  onSelectTool,
  onToggleCollapsed,
  width,
  onResizePointerDown,
  mobile = false,
}: MeetingToolsRailProps) {
  const sections = getMeetingToolSections(role, { canManageTopics });
  const scrollRef = useScrollContainerWheel<HTMLDivElement>();

  // ---------------------------------------------------------------
  // step 1: Collapsed — thin expand strip (desktop only)
  // ---------------------------------------------------------------
  if (collapsed && !mobile) {
    return (
      <button
        type="button"
        onClick={onToggleCollapsed}
        aria-label="Expand Meeting Tools"
        title="Meeting Tools"
        style={{
          flexShrink: 0,
          width: 36,
          height: "100%",
          border: "none",
          borderLeft: "1px solid rgba(255,255,255,0.1)",
          background: "#141820",
          color: "#64748b",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <ChevronLeft
          size={16}
          style={{ transform: "rotate(180deg)" }}
        />
      </button>
    );
  }

  // ---------------------------------------------------------------
  // step 2: Expanded tool list (sidebar or mobile sheet)
  // ---------------------------------------------------------------
  return (
    <aside
      style={
        mobile
          ? {
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1240,
              width: "100%",
              maxHeight: "min(78dvh, 640px)",
              height: "min(78dvh, 640px)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              borderTop: "1px solid rgba(255,255,255,0.12)",
              background: "#141820",
              boxShadow: "0 -16px 48px rgba(0,0,0,0.5)",
              boxSizing: "border-box",
              paddingBottom: "env(safe-area-inset-bottom, 0px)",
            }
          : {
              position: "relative",
              flexShrink: 0,
              width,
              maxWidth: "45vw",
              height: "100%",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              borderLeft: "1px solid rgba(255,255,255,0.1)",
              background: "#141820",
            }
      }
    >
      {mobile ? null : (
        <MeetingToolsResizeHandle onPointerDown={onResizePointerDown} />
      )}
      <div
        style={{
          flexShrink: 0,
          padding: mobile ? "14px 16px 10px" : "12px 16px",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: 13,
            fontWeight: 700,
            color: "#fff",
          }}
        >
          Meeting Tools
        </h2>
        {mobile ? (
          <button
            type="button"
            onClick={onToggleCollapsed}
            aria-label="Close Meeting Tools"
            style={{
              border: "none",
              background: "rgba(255,255,255,0.08)",
              color: "#94a3b8",
              borderRadius: 8,
              padding: "6px 10px",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Close
          </button>
        ) : null}
      </div>

      <div
        ref={scrollRef}
        className="meeting-tool-scroll-body"
        style={{
          flex: 1,
          minHeight: 0,
          height: 0,
          overflowY: "auto",
          overflowX: "hidden",
          padding: "12px 8px",
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
          touchAction: "pan-y",
        }}
      >
        <style>{`
          .meeting-tool-scroll-body {
            scrollbar-width: thin;
            scrollbar-color: rgba(148,163,184,0.55) transparent;
            -webkit-overflow-scrolling: touch;
          }
          .meeting-tool-scroll-body::-webkit-scrollbar { display: block; width: 8px; }
          .meeting-tool-scroll-body::-webkit-scrollbar-thumb {
            background: rgba(148,163,184,0.55);
            border-radius: 999px;
          }
          .meeting-tool-scroll-body::-webkit-scrollbar-track { background: transparent; }
        `}</style>
        {sections.map((section) => (
          <div key={section.title} style={{ marginBottom: 16 }}>
            <p
              style={{
                margin: "0 0 8px",
                padding: "0 8px",
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#64748b",
              }}
            >
              {section.title}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {section.tools.map((tool) => {
                const active = activeTool === tool.id;
                const badge = badgeByTool?.[tool.id];
                return (
                  <button
                    key={tool.id}
                    type="button"
                    onClick={() => onSelectTool(tool.id)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      width: "100%",
                      border: "none",
                      borderRadius: 8,
                      padding: "8px 12px",
                      textAlign: "left",
                      fontSize: 12,
                      fontWeight: 500,
                      cursor: "pointer",
                      background: active
                        ? "rgba(37, 99, 235, 0.3)"
                        : "transparent",
                      color: active ? "#93c5fd" : "#94a3b8",
                    }}
                    onMouseEnter={(e) => {
                      if (active) return;
                      e.currentTarget.style.background =
                        "rgba(255,255,255,0.05)";
                      e.currentTarget.style.color = "#e2e8f0";
                    }}
                    onMouseLeave={(e) => {
                      if (active) return;
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.color = "#94a3b8";
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        flexShrink: 0,
                        opacity: 0.95,
                      }}
                    >
                      {tool.icon}
                    </span>
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {tool.label}
                    </span>
                    {badge != null && badge > 0 ? (
                      <span
                        style={{
                          flexShrink: 0,
                          borderRadius: 999,
                          background: "#f59e0b",
                          color: "#fff",
                          fontSize: 9,
                          fontWeight: 700,
                          padding: "2px 6px",
                        }}
                      >
                        {badge}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={onToggleCollapsed}
        style={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 4,
          border: "none",
          borderTop: "1px solid rgba(255,255,255,0.1)",
          background: "transparent",
          padding: "12px",
          fontSize: 12,
          color: "#64748b",
          cursor: "pointer",
        }}
      >
        <ChevronLeft size={16} />
        {mobile ? "Close Tools" : "Collapse Tools"}
      </button>
    </aside>
  );
}
